/**
 * Building a workflow-tuning request, and turning its result back into graph edits.
 *
 * Two responsibilities here are the frontend's alone, because nothing downstream covers
 * them:
 *
 *  - **`state_ids` are not validated by anyone else.** A typo is accepted with 200, runs
 *    to `succeeded`, spends real tokens and tunes nothing — the unknown id lands silently
 *    in `journal.skipped_targets`. We hold the snapshot for the Workflow page anyway, so
 *    we check the ids against it before spending anything.
 *  - **The proposal is applied by us**, through the connector's admin surface against
 *    `base_revision.digest`.
 */

import type { WorkflowState } from '../types/workflow';
import type {
  WorkflowScope,
  WorkflowTrainConfig,
  WorkflowTuningChange,
  WorkflowTuningJobRequest,
  WorkflowEvaluationJobRequest,
} from '../types/workflowTuning';

// ─── state_ids validation ───────────────────────────────────────────────────

export interface StateIdValidation {
  known: string[];
  unknown: string[];
  /** True when the request would tune nothing but the closing globals iteration. */
  allUnknown: boolean;
}

export function validateStateIds(requested: string[], graphStateIds: string[]): StateIdValidation {
  const available = new Set(graphStateIds);
  const seen = new Set<string>();
  const known: string[] = [];
  const unknown: string[] = [];

  requested
    .map((id) => id.trim())
    .filter(Boolean)
    .forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      (available.has(id) ? known : unknown).push(id);
    });

  return { known, unknown, allUnknown: requested.length > 0 && known.length === 0 };
}

/**
 * Отказ запуска по неизвестным состояниям (AIT-294/AIT-295).
 *
 * Раньше бэкенд молча выбрасывал неизвестные `state_ids`, и задача доходила до
 * `succeeded`, потратив токены и не изменив ничего. Теперь она отвечает `400`:
 *
 *     { "code": "workflow_state_unknown",
 *       "unknown": ["sesion_close"],
 *       "known": ["greeting", "session_close", ...] }
 *
 * Разбирать нужно и после клиентской проверки: та сверяет id со снимком графа,
 * а сервер — с текущим. Граф на деве живой, и между загрузкой снимка и запуском
 * состояние успевает исчезнуть — тогда отказ придёт именно отсюда.
 */
export interface UnknownStatesRefusal {
  unknown: string[];
  known: string[];
}

export const UNKNOWN_STATES_CODE = 'workflow_state_unknown';

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.trim() !== '') : [];
}

/** `null`, если это другая ошибка: подменять чужой отказ этим текстом нельзя. */
export function parseUnknownStatesRefusal(err: unknown): UnknownStatesRefusal | null {
  const body = (err as { response?: { data?: unknown } } | null)?.response?.data ?? err;
  if (!body || typeof body !== 'object') return null;
  // Оболочка проекта — `{ success, error: {...} }`, но ручка может ответить и плоско.
  const candidates: unknown[] = [body, (body as { error?: unknown }).error, (body as { data?: unknown }).data];
  for (const c of candidates) {
    if (!c || typeof c !== 'object') continue;
    const rec = c as Record<string, unknown>;
    if (rec.code !== UNKNOWN_STATES_CODE) continue;
    const unknown = stringList(rec.unknown);
    if (unknown.length === 0) continue;
    return { unknown, known: stringList(rec.known) };
  }
  return null;
}

/** Человеческий текст отказа: что не найдено и на что это похоже. */
export function describeUnknownStatesRefusal(refusal: UnknownStatesRefusal): string[] {
  return refusal.unknown.map((id) => {
    const near = suggestStateIds(id, refusal.known);
    return near.length ? `${id} — did you mean ${near.join(', ')}?` : id;
  });
}

/** Closest graph state ids to a mistyped one, for a "did you mean" hint. */
export function suggestStateIds(unknownId: string, graphStateIds: string[], limit = 3): string[] {
  const needle = unknownId.toLowerCase();
  return graphStateIds
    .map((id) => {
      const haystack = id.toLowerCase();
      let score = 0;
      if (haystack === needle) score = 100;
      else if (haystack.startsWith(needle) || needle.startsWith(haystack)) score = 80;
      else if (haystack.includes(needle) || needle.includes(haystack)) score = 60;
      else {
        // Cheap overlap: shared 3-grams. Enough to catch a transposed or dropped letter.
        const grams = new Set<string>();
        for (let i = 0; i + 3 <= needle.length; i += 1) grams.add(needle.slice(i, i + 3));
        let hits = 0;
        for (let i = 0; i + 3 <= haystack.length; i += 1) if (grams.has(haystack.slice(i, i + 3))) hits += 1;
        score = hits;
      }
      return { id, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.id);
}

// ─── Request building ───────────────────────────────────────────────────────

/**
 * Zero is a JSON default, not a choice — the service ignores it, so we drop it rather
 * than send a field that reads as "explicitly zero".
 */
function positiveOnly(value: number | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

export function buildTrainConfig(raw: Partial<Record<keyof WorkflowTrainConfig, number | undefined>>): WorkflowTrainConfig | undefined {
  const out: WorkflowTrainConfig = {};
  (Object.keys(raw) as Array<keyof WorkflowTrainConfig>).forEach((key) => {
    const value = positiveOnly(raw[key]);
    if (value !== undefined) out[key] = value;
  });
  return Object.keys(out).length > 0 ? out : undefined;
}

export interface WorkflowScopeForm {
  stateIds?: string[];
  parallelIterations?: number;
  allowFormatRequirements?: boolean;
  includeOntology?: boolean;
  includeKnowledgeBase?: boolean;
  readOnlyContext?: boolean;
  containerGrowthHeadroom?: number;
  limits?: { maxStates?: number | null; maxRoles?: number | null; maxCards?: number | null; maxGlobalSections?: number | null };
}

export function buildWorkflowScope(form: WorkflowScopeForm): WorkflowScope {
  const scope: WorkflowScope = {};
  const stateIds = (form.stateIds || []).map((id) => id.trim()).filter(Boolean);
  if (stateIds.length > 0) scope.state_ids = stateIds;
  if (form.parallelIterations && form.parallelIterations > 1) scope.parallel_iterations = form.parallelIterations;
  if (form.allowFormatRequirements) scope.allow_format_requirements = true;
  if (form.includeOntology) scope.include_ontology = true;
  if (form.includeKnowledgeBase) scope.include_knowledge_base = true;
  if (form.readOnlyContext) scope.read_only_context = true;
  if (form.containerGrowthHeadroom && form.containerGrowthHeadroom > 0) {
    scope.container_growth_headroom = form.containerGrowthHeadroom;
  }
  // An unset limit means "no limit"; sending nulls would be the same thing, so only
  // real numbers are worth the bytes.
  const limits: Record<string, number> = {};
  if (positiveOnly(form.limits?.maxStates ?? undefined)) limits.max_states = form.limits!.maxStates as number;
  if (positiveOnly(form.limits?.maxRoles ?? undefined)) limits.max_roles = form.limits!.maxRoles as number;
  if (positiveOnly(form.limits?.maxCards ?? undefined)) limits.max_cards = form.limits!.maxCards as number;
  if (positiveOnly(form.limits?.maxGlobalSections ?? undefined)) {
    limits.max_global_sections = form.limits!.maxGlobalSections as number;
  }
  if (Object.keys(limits).length > 0) scope.limits = limits;
  return scope;
}

export interface WorkflowTuningForm extends WorkflowScopeForm {
  integrationId: string;
  datasetNames: string[];
  approach?: WorkflowTuningJobRequest['approach'];
  evaluationMode?: string;
  evaluatorParams?: Record<string, unknown>;
  selectionScenario?: string;
  numSteps?: number;
  stand?: string;
  trainConfig?: Partial<Record<keyof WorkflowTrainConfig, number | undefined>>;
  maxAlgorithmIterations?: number;
}

export function buildWorkflowTuningRequest(form: WorkflowTuningForm): WorkflowTuningJobRequest {
  const trainConfig = form.trainConfig ? buildTrainConfig(form.trainConfig) : undefined;
  return {
    integration_id: form.integrationId,
    dataset_name: form.datasetNames.length === 1 ? form.datasetNames[0] : form.datasetNames,
    ...(form.approach ? { approach: form.approach } : {}),
    ...(form.evaluationMode ? { evaluation_mode: form.evaluationMode } : {}),
    ...(form.evaluatorParams && Object.keys(form.evaluatorParams).length > 0
      ? { evaluator_params: form.evaluatorParams }
      : {}),
    ...(form.selectionScenario ? { selection_scenario: form.selectionScenario } : {}),
    ...(positiveOnly(form.numSteps) ? { num_steps: form.numSteps } : {}),
    ...(trainConfig ? { train_config: trainConfig } : {}),
    state_machine: {
      stand: form.stand?.trim() || 'dev',
      workflow: buildWorkflowScope(form),
      ...(positiveOnly(form.maxAlgorithmIterations) ? { max_algorithm_iterations: form.maxAlgorithmIterations } : {}),
    },
  };
}

export function buildWorkflowEvaluationRequest(form: {
  integrationId: string;
  datasetNames: string[];
  batchSize?: number;
  evaluationMode?: string;
  datasetSplit?: string;
  batchOffset?: number;
  evaluatorParams?: Record<string, unknown>;
  stand?: string;
}): WorkflowEvaluationJobRequest {
  return {
    integration_id: form.integrationId,
    dataset_name: form.datasetNames.length === 1 ? form.datasetNames[0] : form.datasetNames,
    ...(positiveOnly(form.batchSize) ? { batch_size: form.batchSize } : {}),
    ...(form.evaluationMode ? { evaluation_mode: form.evaluationMode } : {}),
    ...(form.datasetSplit ? { dataset_split: form.datasetSplit } : {}),
    ...(positiveOnly(form.batchOffset) ? { batch_offset: form.batchOffset } : {}),
    ...(form.evaluatorParams && Object.keys(form.evaluatorParams).length > 0
      ? { evaluator_params: form.evaluatorParams }
      : {}),
    state_machine: { stand: form.stand?.trim() || 'dev', workflow: {} },
  };
}

// ─── Change paths ───────────────────────────────────────────────────────────

export interface ParsedChangePath {
  kind: 'state' | 'global' | 'other';
  /** State id or global section name. */
  ident: string;
  /** Remaining path segments inside that state / section. */
  segments: string[];
  /** Human-readable tail, e.g. `agents · coordinator · prompt`. */
  label: string;
}

export function parseChangePath(path: string): ParsedChangePath {
  const segments = path.split('/').filter(Boolean);
  if (segments[0] === 'states' && segments.length >= 2) {
    const rest = segments.slice(2);
    return { kind: 'state', ident: segments[1], segments: rest, label: rest.join(' · ') || 'state' };
  }
  if (segments[0] === 'global' && segments.length >= 2) {
    const rest = segments.slice(2);
    return { kind: 'global', ident: segments[1], segments: rest, label: rest.join(' · ') || 'section' };
  }
  return { kind: 'other', ident: path, segments, label: path };
}

/**
 * Global sections cannot be written from this connector — only `workflow` is writable,
 * everything else answers 403. Applying is therefore limited to `/states/…` changes.
 */
export function isApplicableChange(change: WorkflowTuningChange): boolean {
  return parseChangePath(change.path).kind === 'state';
}

// ─── Applying changes to the graph ──────────────────────────────────────────

function setAtSegments(target: unknown, segments: string[], value: unknown): unknown {
  if (segments.length === 0) return value;
  const [head, ...rest] = segments;

  if (Array.isArray(target)) {
    const index = Number(head);
    if (!Number.isInteger(index) || index < 0) return target;
    const next = [...target];
    // Growing an array here would invent list items the proposal did not describe;
    // an out-of-range index means the graph moved, and is left alone.
    if (index >= next.length) return target;
    next[index] = setAtSegments(next[index], rest, value);
    return next;
  }

  const base = target && typeof target === 'object' ? (target as Record<string, unknown>) : {};
  return { ...base, [head]: setAtSegments(base[head], rest, value) };
}

export interface ApplyChangesResult {
  states: Record<string, WorkflowState>;
  applied: WorkflowTuningChange[];
  /** Changes whose state no longer exists in the graph — the graph moved under the proposal. */
  missed: WorkflowTuningChange[];
}

/**
 * Fold selected changes into the current graph states. Only `/states/…` paths are
 * touched, and a change whose state is gone is reported rather than silently dropped.
 */
export function applyChangesToStates(
  states: Record<string, WorkflowState>,
  changes: WorkflowTuningChange[]
): ApplyChangesResult {
  let next = states;
  const applied: WorkflowTuningChange[] = [];
  const missed: WorkflowTuningChange[] = [];

  changes.forEach((change) => {
    const parsed = parseChangePath(change.path);
    if (parsed.kind !== 'state') return;
    if (!next[parsed.ident]) {
      missed.push(change);
      return;
    }
    const updated = setAtSegments(next[parsed.ident], parsed.segments, change.after) as WorkflowState;
    next = { ...next, [parsed.ident]: updated };
    applied.push(change);
  });

  return { states: next, applied, missed };
}

// ─── Reading the summary honestly ───────────────────────────────────────────

export type CandidateVerdict = 'accepted-improved' | 'accepted-worse' | 'rejected' | 'unknown';

/**
 * `candidate_accepted: true` with a negative delta happened on the live dev run — showing
 * "accepted" alone would read as an improvement. The verdict always folds in `scores`.
 */
export function candidateVerdict(
  accepted: boolean | null | undefined,
  scores: { delta?: number; improved?: boolean } | undefined
): CandidateVerdict {
  if (accepted == null) return 'unknown';
  if (!accepted) return 'rejected';
  const improved = scores?.improved ?? (typeof scores?.delta === 'number' ? scores.delta > 0 : undefined);
  return improved === false ? 'accepted-worse' : 'accepted-improved';
}
